import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { API_ENDPOINTS } from "./apiConfig";
import { getStatusMessage } from "./statusMessages";
import axios from "axios";

function ChangePassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const { email, rotp } = location.state || {};

  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email) {
      navigate("/ForgotPassword", { replace: true });
    }
  }, [email, navigate]);

  // Validation functions
  const validateOtp = (value) => {
    const trimmed = value.trim();
    if (trimmed.length !== 6) return "OTP should be 6 characters.";
    if (!/^[\x20-\x7E]*$/.test(trimmed))
      return "OTP contains invalid characters.";
    return "";
  };

  const validatePassword = (value) => {
    const trimmed = value.trim();
    if (trimmed.length < 8 || trimmed.length > 12)
      return "Password must be between 8 and 12 characters long.";
    if (!/^[\x20-\x7E]*$/.test(trimmed))
      return "Password contains invalid characters.";
    return "";
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setMessage("");
    setLoading(true);

    const otpError = validateOtp(otp);
    const passwordError = validatePassword(password);

    if (otpError || passwordError) {
      setErrors([otpError, passwordError].filter(Boolean));
      setLoading(false);
      return;
    }

    try {
      const res = await axios.post(API_ENDPOINTS.CHANGEPSWD, {
        email,
        OTP: otp,
        password,
        device: "web",
      });

      const { result, user, opt, errors: serverErrors } = res.data;

      if (result === 1) {
        setMessage(getStatusMessage(opt));
        setTimeout(() => navigate("/login"), 2000);
      } else {
        const allMessages = [];
        if (Array.isArray(serverErrors)) allMessages.push(...serverErrors);
        if (user !== undefined) allMessages.push(getStatusMessage(user));
        if (opt !== undefined) allMessages.push(getStatusMessage(opt));

        setErrors(
          allMessages.length ? allMessages : ["Change password failed."]
        );
      }
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;

      if (status === 400 && Array.isArray(data?.errors)) {
        setErrors(data.errors);
      } else if (status === 400 && (data?.user || data?.opt)) {
        const failMessages = [];
        if (data.opt !== undefined)
          failMessages.push(getStatusMessage(data.opt));
        setErrors(failMessages);
      } else {
        setErrors(["Server error. Please try again later."]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = () => {
    navigate("/ForgotPassword");
  };

  return (
    <div
      className="d-flex flex-column min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffe0f0, #d0e8ff)",
        padding: "16px",
        paddingTop: "2rem",
      }}
    >
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Change Password | LuvMarry</title>
        <meta
          name="description"
          content="Reset your password securely on LuvMarry. Enter OTP and create a new password to regain access to your account."
        />
        <meta
          name="keywords"
          content="change password, reset password, LuvMarry, Indian dating USA, Indian marriage website USA"
        />
        <link rel="canonical" href="https://usa.luvmarry.com/ChangePassword" />
      </Helmet>

      <div style={{ maxWidth: 480, width: "100%", margin: "0 auto" }}>
        <h1 className="text-center wedding-heading">Change Password 🔐</h1>

        {/* Show email and OTP message */}
        <div
          className="alert alert-info text-center mb-4"
          style={{ backgroundColor: "#f0f8ff", color: "#333" }}
          role="alert"
        >
          <p>
            Email: <strong>{email}</strong>
          </p>
          {rotp && (
            <div className="mt-2" style={{ fontSize: "0.95rem" }}>
              {getStatusMessage(rotp)}
            </div>
          )}
          <button
            className="btn btn-sm btn-outline-primary mt-2"
            onClick={handleChangeEmail}
          >
            Change Email
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-3">
            <label htmlFor="otp" className="form-label text-muted">
              Enter OTP
            </label>
            <input
              id="otp"
              type="text"
              className="form-control"
              placeholder="6-digit OTP"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              autoComplete="one-time-code"
              required
            />
          </div>

          <div className="mb-3">
            <label htmlFor="password" className="form-label text-muted">
              New Password
            </label>
            <input
              id="password"
              type="password"
              className="form-control"
              placeholder="8-12 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            className="btn w-100 mt-3 d-flex justify-content-center align-items-center gap-2"
            style={{ backgroundColor: "#ff69b4", color: "white" }}
            disabled={loading}
          >
            <i className="bi bi-shield-lock-fill"></i>
            {loading ? "Changing Password..." : "Change Password"}
          </button>
        </form>

        {message && (
          <div
            className="alert alert-success mt-3"
            style={{ whiteSpace: "pre-line" }}
            role="alert"
          >
            {message}
          </div>
        )}

        {errors.length > 0 && (
          <div
            className="alert alert-danger mt-3"
            style={{ backgroundColor: "#ffe0e0", color: "#cc0000" }}
            role="alert"
          >
            <ul className="mb-0">
              {errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChangePassword;
